import { useState, useEffect } from "react";
import { Switch, notification, Tooltip } from "antd";
import { CircleHelp } from "lucide-react";
import { MIN_POINTS_DISCOUNT_THB } from "constants/redemption";
import { useGetCapsulePoints } from "services/user";
import {
  useGetRedemptionConfig,
  useApplyRedemption,
} from "services/redemption";

interface PointsToggleProps {
  onApplied: (discount: number, pointsUsed?: number) => void;
  orderAmount: number;
  cartItemIds: number[];
  promotionCode?: string;
  redemptionItemsPoints?: number; // Total points used by redemption items in cart
}

const PointsToggle: React.FC<PointsToggleProps> = ({
  onApplied,
  orderAmount,
  cartItemIds,
  promotionCode,
  redemptionItemsPoints = 0,
}) => {
  const [useCoins, setUseCoins] = useState<boolean>(false);
  const [previousState, setPreviousState] = useState<boolean>(false);

  // Get capsule points data
  const { capsulePoints } = useGetCapsulePoints();

  // Get redemption configuration
  const { redemptionConfig, isLoading: configLoading } =
    useGetRedemptionConfig();

  // Apply redemption mutation
  const { applyRedemptionAsync, isApplying, applicationError } =
    useApplyRedemption();

  // Get available balance from capsule points, minus points already used for redemption items
  const balance = (capsulePoints?.availablePoints ?? 0) - redemptionItemsPoints;

  // Calculate maximum usable points based on redemption config
  const calculateMaxUsablePoints = (): number => {
    if (!redemptionConfig || !capsulePoints || balance === 0) {
      return 0;
    }

    // Calculate max points based on order amount percentage limit
    const maxByPercentage = Math.floor(
      (orderAmount * redemptionConfig.maxUsagePercentage) /
        100 /
        redemptionConfig.pointsToThbRate
    );

    // The maximum usable points is the lesser of user's balance or the percentage limit
    return Math.min(balance, maxByPercentage);
  };

  const maxUsablePoints = calculateMaxUsablePoints();
  const maxUsableAmount =
    maxUsablePoints * (redemptionConfig?.pointsToThbRate ?? 0);

  const handleSwitch = async (checked: boolean) => {
    // Store previous state for rollback
    setPreviousState(useCoins);

    try {
      if (checked) {
        // Enforce minimum points discount: block if the usable amount is too low
        if (maxUsableAmount < MIN_POINTS_DISCOUNT_THB) {
          setUseCoins(false);
          notification.error({
            message: "ไม่สามารถใช้คะแนนเป็นส่วนลดได้",
            description: `ใช้คะแนนเป็นส่วนลดได้ขั้นต่ำ ${MIN_POINTS_DISCOUNT_THB} บาท`,
            duration: 4,
          });
          return;
        }

        // When enabling points usage, apply points as cash discount
        const response = await applyRedemptionAsync({
          pointsToUse: maxUsablePoints,
          cartItemIds: cartItemIds,
          promotionCode: promotionCode,
        });

        if (response.success) {
          setUseCoins(checked);
          onApplied(response.discountAmount, response.pointsUsed);

          notification.success({
            message: "ใช้คะแนนสำเร็จ",
            description: `ใช้คะแนน ${response.pointsUsed.toLocaleString()} คะแนน ลด ${response.discountAmount.toLocaleString()} บาท`,
            duration: 3,
          });
        } else {
          // Rollback on failure
          setUseCoins(previousState);
          notification.error({
            message: "ไม่สามารถใช้คะแนนได้",
            description:
              response.message ||
              "เกิดข้อผิดพลาดในการใช้คะแนน กรุณาลองใหม่อีกครั้ง",
            duration: 4,
          });
        }
      } else {
        // When disabling points usage, remove the discount
        setUseCoins(checked);
        onApplied(0, 0);

        notification.info({
          message: "ยกเลิกการใช้คะแนน",
          description: "ยกเลิกการใช้คะแนนเป็นส่วนลดเรียบร้อยแล้ว",
          duration: 2,
        });
      }
    } catch (error: unknown) {
      // Rollback on error
      setUseCoins(previousState);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง";

      notification.error({
        message: "เกิดข้อผิดพลาด",
        description: errorMessage,
        duration: 4,
      });
    }
  };

  // If coins are turned off, always notify parent of 0 discount
  useEffect(() => {
    if (!useCoins) {
      onApplied(0, 0);
    }
  }, [useCoins, onApplied]);

  const isLoading = isApplying || configLoading;
  const isDisabled = balance === 0 || maxUsablePoints <= 0 || isLoading;

  return (
    <div>
      <div className="w-full bg-[#F8F8F9] p-2 px-4 justify-between flex items-center">
        <div className="flex flex-col gap-1">
          <div className="text-xs text-green-600 flex items-center gap-1">
            {maxUsableAmount <= 0 ? (
              <div>ไม่สามารถใช้คะแนนได้ เนื่องจากคะแนนไม่เพียงพอ</div>
            ) : (
              <div className="flex gap-2 items-center">
                ใช้คะแนนลด {maxUsableAmount.toLocaleString()} บาท
                {maxUsablePoints > 0 && (
                  <small> ( {maxUsablePoints.toLocaleString()} คะแนน )</small>
                )}
                <Tooltip
                  title={
                    <div className="text-xs">
                      <div>
                        {redemptionConfig?.pointsToThbRate ? (
                          <span>
                            1 บาท ={" "}
                            {(1 / redemptionConfig?.pointsToThbRate).toFixed(2)}
                            คะแนน
                          </span>
                        ) : (
                          <></>
                        )}
                      </div>
                      <div>
                        ใช้ได้สูงสุด: {redemptionConfig?.maxUsagePercentage}%
                        ของยอดสั่งซื้อ
                      </div>
                    </div>
                  }
                >
                  <CircleHelp size={12} className="text-gray-400 cursor-help" />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <Switch
          checked={useCoins}
          onChange={handleSwitch}
          disabled={isDisabled}
          loading={isLoading}
        />
      </div>
      {applicationError && (
        <div className="text-xs text-danger-500 px-4 mt-1">
          เกิดข้อผิดพลาดในการใช้คะแนน กรุณาลองใหม่อีกครั้ง
        </div>
      )}
    </div>
  );
};

export default PointsToggle;
